// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { utils } from './utils';

export interface AppOptions {
  readonly meta: {
    readonly title: string;
    readonly name: string;
    readonly description: string;
    readonly social: {
      readonly twitter: string;
      readonly fb: string;
      readonly instagram: string;
      readonly tiktok: string;
      readonly youtube: string;
      readonly threads: string;
    };
    readonly twitter: {
      readonly handle: string;
      readonly creatorHandle: string;
    };
  };
  readonly url: string;
  readonly reportURL?: string;
  readonly reportTimer?: number;
  readonly maintenance: boolean;
  readonly maintenanceDurationMinutes?: number;
  readonly maintenanceStartTime?: number;
  readonly confirmLimitMS: number;
  readonly debug: boolean;
  readonly prod: boolean;
  readonly googleClientId: string;
  readonly facebookAppId: string;
}
export type EnvironmentType = 'prod' | 'staging' | 'local';
export type DarkModePreference = (typeof DARK_MODE)[keyof typeof DARK_MODE];
export type ColorModeType = 'light' | 'dark';
export const DARK_MODE = {
  SYSTEM: 'system',
  LIGHT: 'light',
  DARK: 'dark',
} as const;

export const FREE_TRIAL_DURATION_SECONDS = 30 * 24 * 60 * 60;
export const FREE_TRIAL_EXPIRATION_GRACE_PERIOD_SECONDS = 3 * 24 * 60 * 60;
export const CHECKOUT_NOT_FOUND_ERROR_MSG =
  'Checkout ID not found! You likely already completed this checkout. Please go to your account page or the checkout page and try again.';
export const SUBSCRIPTION_STATUS = {
  // For very first payment of a sub. Payment failed or incomplete
  INCOMPLETE: 'incomplete',
  // If incomplete for 23 hours and sub is effectively cancelled
  INCOMPLETE_EXPIRED: 'incomplete_expired',
  TRIALING: 'trialing',
  ACTIVE: 'active',
  // Most recent invoice failed or hasn't been attempted
  PAST_DUE: 'past_due',
  CANCELED: 'canceled',
  UNPAID: 'unpaid',
  // Special internal Microgreen Manager use free pass to allow use to use app after trial without having to pay
  _INTERNAL_FREE_PASS: '_internal_free_pass',
} as const;
export type SubscriptionStatusType = (typeof SUBSCRIPTION_STATUS)[keyof typeof SUBSCRIPTION_STATUS];
export const SUBSCRIPTION_TIER = {
  FREE_TRIAL: 'free_trial',
  BASIC_MONTHLY: 'basic_monthly',
  BASIC_YEARLY: 'basic_yearly',
} as const;
export type SubscriptionTierType = (typeof SUBSCRIPTION_TIER)[keyof typeof SUBSCRIPTION_TIER];
export const subscriptionTierToDisplayName = (tier: SubscriptionTierType) => {
  switch (tier) {
    case SUBSCRIPTION_TIER.FREE_TRIAL:
      return 'Free Trial';
    case SUBSCRIPTION_TIER.BASIC_MONTHLY:
      return 'Monthly Subscription';
    case SUBSCRIPTION_TIER.BASIC_YEARLY:
      return 'Yearly Subscription';
    default:
      return tier;
  }
};

export const getPaymentStatus = ({
  subStatus,
  freeTrialEndsAt,
}: {
  readonly subStatus: SubscriptionStatusType;
  readonly freeTrialEndsAt: number | null;
}): 'good' | 'free_trial_ended' | 'payment_failed' => {
  // Free trial hasn't started yet
  if (freeTrialEndsAt == null) {
    return 'good';
  }
  // Free trial hasn't ended yet
  const freeTrialHasEnded = utils.nowSeconds() >= freeTrialEndsAt;
  if (!freeTrialHasEnded) {
    return 'good';
  }
  // If free trial has ended and subscription tier is still free trial then show banner
  if (subStatus === SUBSCRIPTION_STATUS.TRIALING) {
    return 'free_trial_ended';
  }
  if (subStatus === SUBSCRIPTION_STATUS.ACTIVE) {
    return 'good';
  }

  return 'payment_failed';
};
export const DATE_FORMAT = {
  // Common in most countries outside North America
  'DD/MM/YYYY': 'DD/MM/YYYY',
  // Used in India and several European countries
  'DD-MM-YYYY': 'DD-MM-YYYY',
  // Used in some European countries, like Germany, Switzerland, Estonia
  'DD.MM.YYYY': 'DD.MM.YYYY',
  // Used in the United States
  'MM/DD/YYYY': 'MM/DD/YYYY',
  'MM-DD-YYYY': 'MM-DD-YYYY',
  'MM.DD.YYYY': 'MM.DD.YYYY',
  // Used in some Asian countries, ISO 8601
  'YYYY/MM/DD': 'YYYY/MM/DD',
  'YYYY.MM.DD': 'YYYY.MM.DD',
  // International standard ISO 8601 used in Japan, China
  'YYYY-MM-DD': 'YYYY-MM-DD',
} as const;
export type DateFormat = keyof typeof DATE_FORMAT;
export const DATE_FORMAT_VALUES = Object.values(DATE_FORMAT);
const DATE_FORMAT_VALUES_SET = new Set(DATE_FORMAT_VALUES);
export function isValidDateFormat(format: string): format is DateFormat {
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  return DATE_FORMAT_VALUES_SET.has(format as any);
}
export const DEFAULT_DATE_FORMAT = DATE_FORMAT['MM/DD/YYYY'];

// One week
export const ORDER_CREATION_BUFFER_SECONDS = 7 * 24 * 60 * 60;
// 30 days
export const MIN_ORDER_CREATION_BUFFER_SECONDS = 30 * 24 * 60 * 60;
// 30 days
export const TASK_CREATION_BUFFER_SECONDS = 30 * 24 * 60 * 60;

// Crop values
export const WEIGHT_UNITS = {
  GRAMS: 'grams',
  OUNCES: 'ounces',
} as const;
export const CONVERTIBLE_WEIGHT_UNITS = {
  ...WEIGHT_UNITS,
  POUNDS: 'pounds',
  KG: 'kg',
} as const;
export const CONVERTIBLE_WEIGHT_UNITS_VALUES = Object.values(CONVERTIBLE_WEIGHT_UNITS);
export const WEIGHT_UNITS_SHORT = {
  GRAMS: 'g',
  OUNCES: 'oz',
  POUNDS: 'lbs',
  KG: 'kg',
};
export const isValidWeightUnit = (unit: string): unit is WeightUnit =>
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  WEIGHT_UNITS_VALUES.includes(unit as any);
export const isValidConvertibleWeightUnit = (unit: string): unit is ConvertibleWeightUnit =>
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  CONVERTIBLE_WEIGHT_UNITS_VALUES.includes(unit as any);
export const TRAYS = 'trays';
export type WeightUnitPlusTrays = 'grams' | 'ounces' | 'trays';
export const isValidWeightUnitPlusTrays = (unit: string): unit is WeightUnitPlusTrays =>
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  WEIGHT_UNITS_VALUES.includes(unit as any) || TRAYS === unit;
export const getWeightUnitOptions = (preferredUnit: WeightUnit): WeightUnitPlusTrays[] => {
  return preferredUnit === WEIGHT_UNITS.OUNCES
    ? [WEIGHT_UNITS.OUNCES, WEIGHT_UNITS.GRAMS]
    : [WEIGHT_UNITS.GRAMS, WEIGHT_UNITS.OUNCES];
};
export type ConvertibleWeightUnit = (typeof CONVERTIBLE_WEIGHT_UNITS)[keyof typeof CONVERTIBLE_WEIGHT_UNITS];
export type WeightUnit = (typeof WEIGHT_UNITS)[keyof typeof WEIGHT_UNITS];
export const WEIGHT_UNITS_VALUES = Object.values(WEIGHT_UNITS);
export const WEIGHT_UNIT_PLUS_TRAYS_VALUES = [...WEIGHT_UNITS_VALUES, TRAYS];
export const SOW_DENSITY_UNITS = {
  GRAMS: 'grams',
  OUNCES: 'ounces',
  TEASPOONS: 'teaspoons',
  TABLESPOONS: 'tablespoons',
  CUPS: 'cups',
} as const;
export type SowDensityUnitType = (typeof SOW_DENSITY_UNITS)[keyof typeof SOW_DENSITY_UNITS];
export const SOW_DENSITY_UNITS_VALUES = Object.values(SOW_DENSITY_UNITS);
const SOW_DENSITY_UNITS_VALUES_SET = new Set(SOW_DENSITY_UNITS_VALUES);
export const isValidSowDensityUnit = (unit: string): unit is SowDensityUnitType =>
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  SOW_DENSITY_UNITS_VALUES_SET.has(unit as any);

// Order values
export const ORDER_INPUT_TYPES = [WEIGHT_UNITS.OUNCES, WEIGHT_UNITS.GRAMS, TRAYS];
export const ORDER_STATUS = {
  PLANNED: 'planned',
  IN_PROGRESS: 'in_progress',
  CANCELLED: 'cancelled',
  COMPLETED: 'completed',
} as const;
export type OrderStatusType = (typeof ORDER_STATUS)[keyof typeof ORDER_STATUS];
export const ORDER_STATUS_VALUES = Object.values(ORDER_STATUS);
const ORDER_STATUS_VALUES_SET = new Set(ORDER_STATUS_VALUES);
export const isValidOrderStatus = (status: string): status is OrderStatusType =>
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  ORDER_STATUS_VALUES_SET.has(status as any);

export const MAX_ORDER_WEEKLY_FREQUENCY = 12;
export const MAX_TASK_WEEKLY_FREQUENCY = 52;
export const MAX_TASK_DAILY_FREQUENCY = 21;
export const RECURRING_FREQUENCY_UNITS = {
  DAYS: 'days',
  WEEKS: 'weeks',
  DAY_OF_WEEK: 'dayOfWeek',
  DAY_OF_MONTH: 'dayOfMonth',
  NTH_WEEKDAY_OF_MONTH: 'nthWeekdayOfMonth',
} as const;
export const RECURRING_FREQUENCY_UNITS_VALUES = new Set<string>(Object.values(RECURRING_FREQUENCY_UNITS));

export const PLANTING_STATUS = {
  PLANNED: 'planned',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
} as const;
export type PlantingStatusType = (typeof PLANTING_STATUS)[keyof typeof PLANTING_STATUS];
export const PLANTING_STATUS_VALUES = Object.values(PLANTING_STATUS);
const PLANTING_STATUS_VALUES_SET = new Set(PLANTING_STATUS_VALUES);
export const isValidPlantingStatus = (status: string): status is PlantingStatusType =>
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  PLANTING_STATUS_VALUES_SET.has(status as any);
export const PLANTING_STATE = {
  ALLOCATED: 'allocated',
  SURPLUS: 'surplus',
  BUFFER: 'buffer',
} as const;
export type PlantingState = (typeof PLANTING_STATE)[keyof typeof PLANTING_STATE];
export const PLANTING_STATE_VALUES = Object.values(PLANTING_STATE);
const PLANTING_STATE_VALUES_SET = new Set(PLANTING_STATE_VALUES);
export const isValidPlantingState = (state: string): state is PlantingState =>
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  PLANTING_STATE_VALUES_SET.has(state as any);

// Task values
export const TASK_STATUS = {
  PLANNED: 'planned',
  COMPLETED: 'completed',
} as const;
export const TASK_STATUS_VALUES = Object.values(TASK_STATUS);
const TASK_STATUS_VALUES_SET = new Set(TASK_STATUS_VALUES);
export type TaskStatusType = (typeof TASK_STATUS)[keyof typeof TASK_STATUS];
export const isValidTaskStatus = (status: string): status is TaskStatusType =>
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  TASK_STATUS_VALUES_SET.has(status as any);

// Order affects how they are displayed in crop forms
export const STEP_NAME = {
  SOAK: 'Soak',
  PLANT: 'Plant',
  PLANT_AND_WEIGHTED_GERM: 'Plant and weighted germination',
  PLANT_AND_UNWEIGHTED_GERM: 'Plant and unweighted germination',
  PLANT_AND_HUMIDITY_DOME: 'Plant and humidity dome',
  PLANT_AND_BLACKOUT: 'Plant and blackout',
  WEIGHTED_GERMINATION: 'Weighted germination',
  UNWEIGHTED_GERMINATION: 'Unweighted germination',
  HUMIDITY_DOME: 'Humidity dome',
  BLACKOUT: 'Blackout',
  WATERING: 'Watering',
  UNDER_LIGHTS: 'Under lights',
  HARVEST: 'Harvest',
} as const;
const PRODUCTION_STEP_NAMES = new Set(Object.values(STEP_NAME));
export const PRODUCTION_STEP_NAME_VALUES = Object.values(STEP_NAME);
export const isProductionStep = (stepName: string): stepName is StepNameType =>
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  PRODUCTION_STEP_NAMES.has(stepName as any);
// Lower index means higher priority, meaning should come before other steps
const STEP_NAME_PRIORITY = [
  STEP_NAME.HARVEST,
  STEP_NAME.WATERING,
  STEP_NAME.UNDER_LIGHTS,
  STEP_NAME.HUMIDITY_DOME,
  STEP_NAME.PLANT_AND_HUMIDITY_DOME,
  STEP_NAME.BLACKOUT,
  STEP_NAME.PLANT_AND_BLACKOUT,
  STEP_NAME.UNWEIGHTED_GERMINATION,
  STEP_NAME.PLANT_AND_UNWEIGHTED_GERM,
  STEP_NAME.WEIGHTED_GERMINATION,
  STEP_NAME.PLANT_AND_WEIGHTED_GERM,
  STEP_NAME.PLANT,
  STEP_NAME.SOAK,
];
export const STEP_NAME_TO_PRIORITY = new Map<string, number>(
  STEP_NAME_PRIORITY.map((stepName, idx) => [stepName, idx]),
);
const GERMINATION_STEPS = new Set([
  STEP_NAME.WEIGHTED_GERMINATION,
  STEP_NAME.UNWEIGHTED_GERMINATION,
  STEP_NAME.BLACKOUT,
  STEP_NAME.HUMIDITY_DOME,
  STEP_NAME.PLANT_AND_WEIGHTED_GERM,
  STEP_NAME.PLANT_AND_UNWEIGHTED_GERM,
  STEP_NAME.PLANT_AND_HUMIDITY_DOME,
  STEP_NAME.PLANT_AND_BLACKOUT,
]);
export type GerminationStepType = (typeof STEP_NAME)[
  | 'WEIGHTED_GERMINATION'
  | 'UNWEIGHTED_GERMINATION'
  | 'BLACKOUT'
  | 'HUMIDITY_DOME'
  | 'PLANT_AND_WEIGHTED_GERM'
  | 'PLANT_AND_UNWEIGHTED_GERM'
  | 'PLANT_AND_HUMIDITY_DOME'
  | 'PLANT_AND_BLACKOUT'];
export const GERMINATION_STEP_NAME_VALUES = [...GERMINATION_STEPS];
export const isGerminationStep = (stepName: string): stepName is GerminationStepType =>
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  GERMINATION_STEPS.has(stepName as any);
const PLANT_STEPS = new Set([
  STEP_NAME.PLANT,
  STEP_NAME.PLANT_AND_WEIGHTED_GERM,
  STEP_NAME.PLANT_AND_UNWEIGHTED_GERM,
  STEP_NAME.PLANT_AND_HUMIDITY_DOME,
  STEP_NAME.PLANT_AND_BLACKOUT,
]);
export type PlantStepType = (typeof STEP_NAME)[
  | 'PLANT'
  | 'PLANT_AND_WEIGHTED_GERM'
  | 'PLANT_AND_UNWEIGHTED_GERM'
  | 'PLANT_AND_HUMIDITY_DOME'
  | 'PLANT_AND_BLACKOUT'];
export const PLANT_STEPS_VALUES = [...PLANT_STEPS];
export const isPlantStep = (stepName: string): stepName is PlantStepType =>
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  PLANT_STEPS.has(stepName as any);
const DURATION_STEPS = new Set([
  STEP_NAME.WEIGHTED_GERMINATION,
  STEP_NAME.UNWEIGHTED_GERMINATION,
  STEP_NAME.BLACKOUT,
  STEP_NAME.HUMIDITY_DOME,
  STEP_NAME.UNDER_LIGHTS,
  STEP_NAME.PLANT_AND_WEIGHTED_GERM,
  STEP_NAME.PLANT_AND_UNWEIGHTED_GERM,
  STEP_NAME.PLANT_AND_HUMIDITY_DOME,
  STEP_NAME.PLANT_AND_BLACKOUT,
]);
export type DurationStepType = (typeof STEP_NAME)[
  | 'WEIGHTED_GERMINATION'
  | 'UNWEIGHTED_GERMINATION'
  | 'BLACKOUT'
  | 'HUMIDITY_DOME'
  | 'UNDER_LIGHTS'
  | 'PLANT_AND_WEIGHTED_GERM'
  | 'PLANT_AND_UNWEIGHTED_GERM'
  | 'PLANT_AND_HUMIDITY_DOME'
  | 'PLANT_AND_BLACKOUT'];
export function getCombinedPlantAndGermStep(germStepName: string): string {
  switch (germStepName) {
    case STEP_NAME.WEIGHTED_GERMINATION:
      return STEP_NAME.PLANT_AND_WEIGHTED_GERM;
    case STEP_NAME.UNWEIGHTED_GERMINATION:
      return STEP_NAME.PLANT_AND_UNWEIGHTED_GERM;
    case STEP_NAME.HUMIDITY_DOME:
      return STEP_NAME.PLANT_AND_HUMIDITY_DOME;
    case STEP_NAME.BLACKOUT:
      return STEP_NAME.PLANT_AND_BLACKOUT;
    default:
      return germStepName;
  }
}
export const isDurationStep = (stepName: string): stepName is DurationStepType =>
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  DURATION_STEPS.has(stepName as any);
const STAGE_STEPS = new Set([
  STEP_NAME.PLANT,
  STEP_NAME.SOAK,
  STEP_NAME.WEIGHTED_GERMINATION,
  STEP_NAME.UNWEIGHTED_GERMINATION,
  STEP_NAME.BLACKOUT,
  STEP_NAME.HUMIDITY_DOME,
  STEP_NAME.UNDER_LIGHTS,
  STEP_NAME.PLANT_AND_WEIGHTED_GERM,
  STEP_NAME.PLANT_AND_UNWEIGHTED_GERM,
  STEP_NAME.PLANT_AND_HUMIDITY_DOME,
  STEP_NAME.PLANT_AND_BLACKOUT,
]);
export type StageStepType = (typeof STEP_NAME)[
  | 'PLANT'
  | 'SOAK'
  | 'WEIGHTED_GERMINATION'
  | 'UNWEIGHTED_GERMINATION'
  | 'BLACKOUT'
  | 'HUMIDITY_DOME'
  | 'UNDER_LIGHTS'
  | 'PLANT_AND_WEIGHTED_GERM'
  | 'PLANT_AND_UNWEIGHTED_GERM'
  | 'PLANT_AND_HUMIDITY_DOME'
  | 'PLANT_AND_BLACKOUT'];
export const isStageStep = (stepName: string): stepName is StageStepType =>
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  STAGE_STEPS.has(stepName as any);
export const stageTaskNameToStepName = (name: string) => {
  switch (name) {
    case STEP_NAME.PLANT:
      return 'Planted';
    case STEP_NAME.SOAK:
      return 'Soaking';
    case STEP_NAME.UNWEIGHTED_GERMINATION:
    case STEP_NAME.PLANT_AND_UNWEIGHTED_GERM:
      return STEP_NAME.UNWEIGHTED_GERMINATION;
    case STEP_NAME.BLACKOUT:
    case STEP_NAME.PLANT_AND_BLACKOUT:
      return STEP_NAME.BLACKOUT;
    case STEP_NAME.HUMIDITY_DOME:
    case STEP_NAME.PLANT_AND_HUMIDITY_DOME:
      return STEP_NAME.HUMIDITY_DOME;
    case STEP_NAME.UNDER_LIGHTS:
      return STEP_NAME.UNDER_LIGHTS;
    case STEP_NAME.PLANT_AND_WEIGHTED_GERM:
    case STEP_NAME.WEIGHTED_GERMINATION:
      return STEP_NAME.WEIGHTED_GERMINATION;
    default:
      return name;
  }
};
export const cropStepNameToShortName = (name: string) => {
  switch (name) {
    case STEP_NAME.PLANT:
      return 'Plant';
    case STEP_NAME.PLANT_AND_HUMIDITY_DOME:
    case STEP_NAME.HUMIDITY_DOME:
      return 'Humidity dome';
    case STEP_NAME.PLANT_AND_WEIGHTED_GERM:
    case STEP_NAME.WEIGHTED_GERMINATION:
      return 'Weighted germ';
    case STEP_NAME.UNWEIGHTED_GERMINATION:
    case STEP_NAME.PLANT_AND_UNWEIGHTED_GERM:
      return 'Unweighted germ';
    case STEP_NAME.PLANT_AND_BLACKOUT:
    case STEP_NAME.BLACKOUT:
      return 'Blackout';
    case STEP_NAME.UNDER_LIGHTS:
      return 'Lights';
    case STEP_NAME.HARVEST:
      return name;
    case STEP_NAME.WATERING:
      return 'Water';
    default:
      return name;
  }
};
export const cropStepNameToCalendarLabel = (name: string) => {
  switch (name) {
    case STEP_NAME.PLANT_AND_HUMIDITY_DOME:
      return 'Plant + humidity dome';
    case STEP_NAME.HUMIDITY_DOME:
      return 'Humidity dome';
    case STEP_NAME.PLANT_AND_WEIGHTED_GERM:
      return 'Plant + weighted germ';
    case STEP_NAME.WEIGHTED_GERMINATION:
      return 'Weighted germ';
    case STEP_NAME.PLANT_AND_UNWEIGHTED_GERM:
      return 'Plant + unweighted germ';
    case STEP_NAME.UNWEIGHTED_GERMINATION:
      return 'Unweighted germ';
    case STEP_NAME.PLANT_AND_BLACKOUT:
      return 'Plant + blackout';
    case STEP_NAME.UNDER_LIGHTS:
      return 'Lights';
    case STEP_NAME.WATERING:
      return 'Water';
    case STEP_NAME.PLANT:
    case STEP_NAME.BLACKOUT:
    case STEP_NAME.HARVEST:
      return name;
    default:
      return name;
  }
};
const ALL_STEPS = new Set([...Object.values(STEP_NAME)]);
export type StepNameType = (typeof STEP_NAME)[keyof typeof STEP_NAME];
// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export const isStepName = (name: string): name is StepNameType => ALL_STEPS.has(name as any);
export const STEP_NAME_VALUES = Object.values(STEP_NAME);
export const PLANT_STEP_NAMES = STEP_NAME_VALUES.filter(isPlantStep);
export const getPlantStepNames = () => [...PLANT_STEP_NAMES];

// Recurring Order values
export const RECURRING_ORDER_STATUS = {
  ACTIVE: 'active',
  PAUSED: 'paused',
} as const;
export type RecurringOrderStatusType = (typeof RECURRING_ORDER_STATUS)[keyof typeof RECURRING_ORDER_STATUS];
export const RECURRING_ORDER_STATUS_VALUES = Object.values(RECURRING_ORDER_STATUS);
const RECURRING_ORDER_STATUS_VALUES_SET = new Set(RECURRING_ORDER_STATUS_VALUES);
export const isValidRecurringOrderStatus = (status: string): status is RecurringOrderStatusType =>
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  RECURRING_ORDER_STATUS_VALUES_SET.has(status as any);
export const RECURRING_TASK_STATUS = {
  ACTIVE: 'active',
  PAUSED: 'paused',
} as const;
export type RecurringTaskStatusType = (typeof RECURRING_TASK_STATUS)[keyof typeof RECURRING_TASK_STATUS];
export const RECURRING_TASK_STATUS_VALUES = Object.values(RECURRING_TASK_STATUS);
const RECURRING_TASK_STATUS_VALUES_SET = new Set(RECURRING_TASK_STATUS_VALUES);
export const isValidRecurringTaskStatus = (status: string): status is RecurringTaskStatusType =>
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  RECURRING_TASK_STATUS_VALUES_SET.has(status as any);

// Crop status
export const CROP_STATUS = {
  ACTIVE: 'active',
  ARCHIVED: 'archived',
} as const;
export type CropStatusType = (typeof CROP_STATUS)[keyof typeof CROP_STATUS];
export const CROP_STATUS_VALUES = Object.values(CROP_STATUS);
const CROP_STATUS_VALUES_SET = new Set(CROP_STATUS_VALUES);
export const isValidCropStatus = (status: string): status is CropStatusType =>
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  CROP_STATUS_VALUES_SET.has(status as any);

// Blend status
export const BLEND_STATUS = {
  ACTIVE: 'active',
  ARCHIVED: 'archived',
} as const;
export type BlendStatusType = (typeof BLEND_STATUS)[keyof typeof BLEND_STATUS];
export const BLEND_STATUS_VALUES = Object.values(BLEND_STATUS);
const BLEND_STATUS_VALUES_SET = new Set(BLEND_STATUS_VALUES);
export const isValidBlendStatus = (status: string): status is BlendStatusType =>
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  BLEND_STATUS_VALUES_SET.has(status as any);

// Permissions
export const PERMISSION_TYPE = {
  CAN_EDIT_ORDERS: 'can_edit_orders',
  CAN_EDIT_CROPS_BLENDS: 'can_edit_crops_blends',
  CAN_EDIT_TASKS: 'can_edit_tasks',
  CAN_COMPLETE_TASKS: 'can_complete_tasks',
} as const;
export type PermissionTypeType = (typeof PERMISSION_TYPE)[keyof typeof PERMISSION_TYPE];
export const PERMISSION_TYPE_VALUES = Object.values(PERMISSION_TYPE);
const PERMISSION_TYPE_VALUES_SET = new Set(PERMISSION_TYPE_VALUES);
export const isValidPermissionType = (permission: string): permission is PermissionTypeType =>
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  PERMISSION_TYPE_VALUES_SET.has(permission as any);

// User invites
export const USER_INVITE_STATUS = {
  PENDING: 'pending',
  CANCELLED: 'cancelled',
} as const;
export type UserInviteStatusType = (typeof USER_INVITE_STATUS)[keyof typeof USER_INVITE_STATUS];
export const USER_INVITE_STATUS_VALUES = Object.values(USER_INVITE_STATUS);
export const USER_INVITE_TOKEN_PARAM = 'inviteToken';

export const WEEK_DAY_START = {
  SATURDAY: 'Saturday',
  SUNDAY: 'Sunday',
  MONDAY: 'Monday',
} as const;
export const WEEK_DAY_START_VALUES = Object.values(WEEK_DAY_START);
const WEEK_DAY_START_VALUES_SET = new Set(WEEK_DAY_START_VALUES);
// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export const isValidWeekStart = (day: string): day is WeekStartDayType => WEEK_DAY_START_VALUES_SET.has(day as any);
export type WeekStartDayType = (typeof WEEK_DAY_START)[keyof typeof WEEK_DAY_START];
export const weekDayStartToNumber = (day: WeekStartDayType): number => {
  switch (day) {
    case WEEK_DAY_START.SUNDAY:
      return 0;
    case WEEK_DAY_START.MONDAY:
      return 1;
    case WEEK_DAY_START.SATURDAY:
      return 6;
    default:
      utils.assertNever(day);
      return 0; // Default to Sunday
  }
};

export const AFFILIATE_PARAM = 'aff';
export const FIRSTNAME_PARAM = 'firstname';
export const LASTNAME_PARAM = 'lastname';
export const FARMNAME_PARAM = 'farmname';
export const EMAIL_PARAM = 'email';
export const PASSWORD_RESET_TOKEN_PARAM = 'resetToken';

export const URL_PARAMS = [
  AFFILIATE_PARAM,
  FIRSTNAME_PARAM,
  LASTNAME_PARAM,
  FARMNAME_PARAM,
  EMAIL_PARAM,
  USER_INVITE_TOKEN_PARAM,
];

export const UNAUTHORIZED_ERROR =
  'You must be logged in to do this. Your session may have expired. Try refreshing the page and logging in again.';
export const DEFAULT_CROP_NOTES = 'Created by adding defaults';
